import React from 'react';
import { Modal, Button, Icon, Accordion } from 'semantic-ui-react';

interface HelpModalProps {
  open: boolean;
  onClose: () => void;
}

export const HelpModal: React.FC<HelpModalProps> = ({ open, onClose }) => {
  const panels = [
    {
      key: 'note-info',
      title: {
        content: (
          <span>
            <Icon name="file alternate outline" /> Note Information
          </span>
        ),
      },
      content: {
        content: (
          <div className="help-section">
            <div className="help-item">
              <h4>Property Address / Note Name</h4>
              <p>
                A descriptive identifier for this mortgage note analysis. This can be the property
                address or any name that helps you identify this particular note.
              </p>
              <p><strong>Example:</strong> "123 Main Street, Anytown, USA" or "Smith Note #1"</p>
            </div>
          </div>
        ),
      },
    },
    {
      key: 'purchase-details',
      title: {
        content: (
          <span>
            <Icon name="dollar sign" /> Purchase Details & Fees
          </span>
        ),
      },
      content: {
        content: (
          <div className="help-section">
            <div className="help-item">
              <h4>Listed Purchase Price</h4>
              <p>
                The asking price for the mortgage note before any negotiated discounts. This is
                the base price quoted by the seller.
              </p>
            </div>

            <div className="help-item">
              <h4>Trade Desk Fee</h4>
              <p>
                A one-time fee charged by the note marketplace or broker for facilitating the
                transaction. Typically ranges from $1,500 to $3,000.
              </p>
            </div>

            <div className="help-item">
              <h4>Titling Fees</h4>
              <p>
                One-time costs for transferring legal ownership of the note, including recording
                fees, assignment preparation, and related administrative costs.
              </p>
            </div>

            <div className="help-item">
              <h4>Monthly Servicing Fee</h4>
              <p>
                Recurring monthly fee paid to a loan servicer who handles payment collection,
                borrower communication, escrow management, and compliance. This fee is deducted
                from your total investment cost calculation.
              </p>
              <p className="formula">
                <strong>Impact:</strong> Monthly Servicing Fee × Payments Remaining = Total Servicing Cost
              </p>
            </div>

            <div className="help-item">
              <h4>Other One-Time Fee</h4>
              <p>
                Any additional one-time costs such as due diligence expenses, legal review,
                property inspection, or closing costs not covered elsewhere.
              </p>
            </div>

            <div className="help-item">
              <h4>Other Monthly Fee</h4>
              <p>
                Any additional recurring monthly costs such as insurance escrow shortfalls,
                property monitoring services, or other ongoing expenses.
              </p>
            </div>
          </div>
        ),
      },
    },
    {
      key: 'note-terms',
      title: {
        content: (
          <span>
            <Icon name="calculator" /> Note Terms
          </span>
        ),
      },
      content: {
        content: (
          <div className="help-section">
            <div className="help-item">
              <h4>Interest Rate (%)</h4>
              <p>
                The annual interest rate on the original mortgage note. This is the rate the
                borrower pays on the outstanding principal balance.
              </p>
              <p><strong>Note:</strong> Enter as a percentage (e.g., 12.75 for 12.75%)</p>
            </div>

            <div className="help-item">
              <h4>Monthly P&I Payment</h4>
              <p>
                The borrower's monthly Principal and Interest payment. This is the amount you
                will receive each month as the note holder (before any servicing fees are deducted
                by your servicer).
              </p>
            </div>

            <div className="help-item">
              <h4>Payments Remaining</h4>
              <p>
                The number of monthly payments left until the note is fully paid off. This
                determines the investment duration.
              </p>
              <p className="formula">
                <strong>Conversion:</strong> Years = Payments Remaining ÷ 12
              </p>
            </div>

            <div className="help-item">
              <h4>Desired Purchase Price (Optional)</h4>
              <p>
                The price you want to offer or negotiate for the note. Used in discount analysis
                to compare returns at different purchase prices. Leave blank to use listed price.
              </p>
            </div>
          </div>
        ),
      },
    },
    {
      key: 'key-metrics',
      title: {
        content: (
          <span>
            <Icon name="dashboard" /> Key Metrics Summary
          </span>
        ),
      },
      content: {
        content: (
          <div className="help-section">
            <div className="help-item">
              <h4>Total Investment</h4>
              <p>
                Your complete out-of-pocket cost to acquire and hold the note, including all
                one-time and recurring fees over the life of the loan.
              </p>
              <p className="formula">
                <strong>Formula:</strong><br />
                Listed Purchase Price + Trade Desk Fee + Titling Fees + Other One-Time Fee +
                (Monthly Servicing Fee × Payments Remaining) + (Other Monthly Fee × Payments Remaining)
              </p>
            </div>

            <div className="help-item">
              <h4>Total Return</h4>
              <p>
                The total amount of money you will receive over the life of the note from all
                monthly payments.
              </p>
              <p className="formula">
                <strong>Formula:</strong> Monthly P&I Payment × Payments Remaining
              </p>
            </div>

            <div className="help-item">
              <h4>Annualized ROI</h4>
              <p>
                The compound annual growth rate (CAGR) of your investment. This shows what
                annual return you would need to turn your investment into the total return
                over the investment period.
              </p>
              <p className="formula">
                <strong>Formula:</strong> (Total Return ÷ Total Investment)<sup>(1 ÷ Years)</sup> - 1
              </p>
            </div>

            <div className="help-item">
              <h4>Monthly Income</h4>
              <p>
                The recurring monthly payment you receive from the borrower. Equal to the
                Monthly P&I Payment.
              </p>
            </div>
          </div>
        ),
      },
    },
    {
      key: 'standard-analysis',
      title: {
        content: (
          <span>
            <Icon name="line graph" /> Standard Analysis
          </span>
        ),
      },
      content: {
        content: (
          <div className="help-section">
            <div className="help-item">
              <h4>Purchase Price (with fees)</h4>
              <p>Same as Total Investment - your complete acquisition cost including all fees.</p>
            </div>

            <div className="help-item">
              <h4>Loan Duration</h4>
              <p>The time remaining until the note is fully paid off, expressed in years.</p>
              <p className="formula">
                <strong>Formula:</strong> Payments Remaining ÷ 12
              </p>
            </div>

            <div className="help-item">
              <h4>Monthly Income</h4>
              <p>Your monthly cash flow from the borrower's P&I payment.</p>
            </div>

            <div className="help-item">
              <h4>Annual Income</h4>
              <p>Your yearly cash flow from the note.</p>
              <p className="formula">
                <strong>Formula:</strong> Monthly P&I Payment × 12
              </p>
            </div>

            <div className="help-item">
              <h4>Total Income</h4>
              <p>The sum of all payments you will receive over the life of the note.</p>
              <p className="formula">
                <strong>Formula:</strong> Monthly P&I Payment × Payments Remaining
              </p>
            </div>

            <div className="help-item">
              <h4>Annualized ROI</h4>
              <p>
                Compound Annual Growth Rate (CAGR) - the rate at which your investment grows
                annually on a compounded basis.
              </p>
              <p className="formula">
                <strong>Formula:</strong> (Total Income ÷ Purchase Price)<sup>(1 ÷ Years)</sup> - 1
              </p>
            </div>

            <div className="help-item">
              <h4>Total ROI</h4>
              <p>
                Simple return on investment over the entire holding period. Shows total
                percentage gain relative to your investment.
              </p>
              <p className="formula">
                <strong>Formula:</strong> (Total Income - Purchase Price) ÷ Purchase Price
              </p>
            </div>

            <div className="help-item">
              <h4>IRR (Internal Rate of Return)</h4>
              <p>
                The annualized effective compounded return rate that accounts for the timing
                of cash flows. This is the most accurate measure of investment performance
                for assets with regular periodic payments.
              </p>
              <p className="formula">
                <strong>Formula:</strong> Uses the RATE function (Newton-Raphson iteration) to solve:<br />
                PV × (1 + r)<sup>n</sup> + PMT × [(1 + r)<sup>n</sup> - 1] / r = 0<br />
                Where PV = -Purchase Price, PMT = Monthly Payment, n = Payments Remaining<br />
                Result is multiplied by 12 to annualize the monthly rate.
              </p>
            </div>
          </div>
        ),
      },
    },
    {
      key: 'discount-analysis',
      title: {
        content: (
          <span>
            <Icon name="percent" /> Discount Analysis
          </span>
        ),
      },
      content: {
        content: (
          <div className="help-section">
            <div className="help-item">
              <h4>Discount Percentage</h4>
              <p>
                The percentage reduction from the listed price. Use the slider to explore
                how different discount levels affect your returns.
              </p>
              <p className="formula">
                <strong>Formula:</strong> (Listed Price - Desired Price) ÷ Listed Price × 100
              </p>
            </div>

            <div className="help-item">
              <h4>Discounted Purchase Price</h4>
              <p>The price after applying the discount percentage.</p>
              <p className="formula">
                <strong>Formula:</strong> Listed Price × (1 - Discount Percentage ÷ 100)
              </p>
            </div>

            <div className="help-item">
              <h4>Discounted Investment (with fees)</h4>
              <p>
                Your total investment cost using the discounted purchase price, including
                all fees.
              </p>
              <p className="formula">
                <strong>Formula:</strong><br />
                Discounted Price + Trade Desk Fee + Titling Fees + Other One-Time Fee +
                (Monthly Servicing Fee × Payments Remaining) + (Other Monthly Fee × Payments Remaining)
              </p>
            </div>

            <div className="help-item">
              <h4>Actual Profit (Total Profit)</h4>
              <p>The total profit from the discounted investment over the life of the note.</p>
              <p className="formula">
                <strong>Formula:</strong> Total Income - Discounted Investment (with fees)
              </p>
            </div>

            <div className="help-item">
              <h4>Annual Profit</h4>
              <p>The profit earned per year from the discounted investment.</p>
              <p className="formula">
                <strong>Formula:</strong> (Actual Profit ÷ Payments Remaining) × 12
              </p>
            </div>

            <div className="help-item">
              <h4>Discounted Annualized ROI</h4>
              <p>The CAGR using the discounted investment amount.</p>
              <p className="formula">
                <strong>Formula:</strong> (Total Income ÷ Discounted Investment)<sup>(1 ÷ Years)</sup> - 1
              </p>
            </div>

            <div className="help-item">
              <h4>Discounted Total ROI</h4>
              <p>Simple return on investment using the discounted investment amount.</p>
              <p className="formula">
                <strong>Formula:</strong> (Total Income - Discounted Investment) ÷ Discounted Investment
              </p>
            </div>

            <div className="help-item">
              <h4>Discounted IRR</h4>
              <p>Internal Rate of Return calculated using the discounted investment amount.</p>
              <p className="formula">
                <strong>Formula:</strong> Same IRR calculation but with Discounted Investment as the present value.
              </p>
            </div>

            <div className="help-item">
              <h4>ROI Improvement</h4>
              <p>The increase in Total ROI achieved by negotiating the discount.</p>
              <p className="formula">
                <strong>Formula:</strong> Discounted Total ROI - Standard Total ROI
              </p>
            </div>

            <div className="help-item">
              <h4>IRR Improvement</h4>
              <p>The increase in IRR achieved by negotiating the discount.</p>
              <p className="formula">
                <strong>Formula:</strong> Discounted IRR - Standard IRR
              </p>
            </div>
          </div>
        ),
      },
    },
  ];

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon className="help-modal">
      <Modal.Header>
        <Icon name="help circle" /> Mortgage Note Analyzer - Help Guide
      </Modal.Header>
      <Modal.Content scrolling>
        <p style={{ marginBottom: '1.5rem', color: '#666' }}>
          Click on each section below to expand detailed explanations and calculation formulas.
        </p>
        <Accordion
          fluid
          styled
          panels={panels}
          exclusive={false}
          defaultActiveIndex={[0, 1, 2, 3, 4, 5]}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} primary>
          <Icon name="check" /> Got It
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
